import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';

import { isFinite } from 'lodash';
import clsx from 'clsx';
import {
    Dialog, DialogContent, makeStyles, Grid,
} from '@material-ui/core';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import { Col, Form, Row } from 'react-bootstrap';
import DialogActions from 'components/widgets/modal/DialogActions';
import InputNumber from 'components/widgets/InputNumber';
import DatePicker from 'react-datepicker';
import LoadingMask from 'components/widgets/LoadingMask';
import DateUtils from 'lib/DateUtils';
import { RollPaymentTo } from 'utils/enum/DealEnum';
import CalendarContainer from 'components/widgets/form/CalendarContainer';

import ButtonStyles from 'styles/theme/Button';
import { MEDIA_QUERY } from 'utils/enum/Core';

const ACTION_TYPE = {
    SET_FIELD: 'setField',
    SET_FIRSTPAYMENT_DAYS: 'setFirstPaymentDays',
    SET_FIRSTPAYMENT_DATE: 'setFirstPaymentDate',
    RESET_CALCULATE: 'resetCalculate',
    UPDATE_FIELDS_BY_PROPS: 'updateFieldsByProps',
};

const reducer = (state, action) => {
    const {
        value, type, field,
    } = action;
    const { soldDate } = state;
    switch (type) {
    case ACTION_TYPE.SET_FIELD:
        return {
            ...state,
            [field]: value,
            enableCalculate: true,
        };
    case ACTION_TYPE.SET_FIRSTPAYMENT_DAYS:
        const currentFirstPaymentDue = isFinite(value) ? DateUtils.add(soldDate, value).toDate() : null;
        return {
            ...state,
            firstPaymentDue: currentFirstPaymentDue,
            firstPaymentDueDays: value,
            enableCalculate: true,
        };
    case ACTION_TYPE.SET_FIRSTPAYMENT_DATE:
        const diffDays = DateUtils.diff(DateUtils.format(value), DateUtils.format(soldDate));
        return {
            ...state,
            firstPaymentDue: value,
            firstPaymentDueDays: diffDays,
            enableCalculate: true,
        };
    case ACTION_TYPE.UPDATE_FIELDS_BY_PROPS:
        return {
            ...state,
            ...action.fields,
        };
    case ACTION_TYPE.RESET_CALCULATE:
        return { ...state, enableCalculate: false };
    default:
        return state;
    }
};

const useStyles = makeStyles((theme) => ({
    row: {
        margin: 0,
        marginBottom: 20,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    dialogContent: {
        padding: theme.spacing(3, 4),
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            padding: '8px 12px',
        },
    },
    group: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 5px',
        '& > label': {
            marginBottom: 0,
            marginRight: 10,
            minWidth: 160,
            textAlign: 'end',
            color: theme.palette.text.minsk,
            fontSize: '14px',
            fontWeight: 500,
        },
        '& > *:last-child': {
            flex: 1,
        },
    },
    styleColLeft: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '5px 0',
    },
    alignText: {
        textAlign: 'center',
    },
    alignTextLeft: {
        textAlign: 'left',
        '& label': {
            cursor: 'pointer',
            color: '#767676',
            fontSize: '14px !important',
            fontWeight: 'bold !important',
            marginBottom: '2px !important',
            paddingLeft: '36px',
        },
    },
    labelStyle: {
        marginBottom: '2px !important',
        fontWeight: 'bold !important',
        fontSize: '14px !important',
        color: '#767676',
    },
    switchMargin: {
        marginLeft: '10px',
    },
    emptyLabel: {
        minHeight: '14px',
        display: 'block',
    },
    calculateButton: {
        width: '200px',
        margin: '0 auto 0 0',
    },
    noMarginBottom: {
        marginBottom: 0,
    },
    loadingSpinner: {
        position: 'absolute',
        opacity: 0.6,
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
        zIndex: 9999,
    },
    box: {
        marginTop: '10px',
        padding: '5px',
        border: `solid 1px ${theme.palette.border.mercury}`,
        display: 'block',
    },
    hidden: {
        visibility: 'hidden',
    },
    alignLabel: {
        '& > label': {
            float: 'right',
            marginRight: '60px',
        },
    },
    radio: {
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingLeft: 0,
        paddingRight: 40,
        '& input[type="radio"]': {
            position: 'relative',
            marginLeft: theme.spacing(1),
            marginRight: 0,
            marginTop: 0,
        },
        '& label': {
            cursor: 'pointer',
            color: '#767676',
            fontSize: '14px !important',
            fontWeight: 'bold !important',
            marginBottom: '0 !important',
            paddingLeft: 0,
            whiteSpace: 'nowrap',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            '& label': {
                fontSize: '13px !important',
            },
        },
    },
    ...ButtonStyles.getStyle(theme),
}));

const RollPaymentDialog = ({
    toggleModal,
    term,
    regZAPR,
    soldDate,
    loading,
    interestRate,
    calculateDeal,
    rollPaymentTo,
    paymentAmount,
    financeAmount,
    finalPaymentDue,
    firstPaymentDue,
    finalPaymentAmount,
    firstPaymentDueDays,
    resetDealInformation,
    minimumPaymentOverride,
    rollPaymentApplyEnabled,
}) => {
    const classes = useStyles();
    const maximumPaymentOverride = financeAmount / 2;
    const tooltip = 'Before applying changes please click on Calculate button';

    const [state, dispatch] = useReducer(reducer, {
        term,
        soldDate,
        interestRate,
        rollPaymentTo,
        paymentAmount,
        firstPaymentDue,
        firstPaymentDueDays,
        enableCalculate: false,
    });

    useEffect(() => {
        dispatch({
            type: ACTION_TYPE.UPDATE_FIELDS_BY_PROPS,
            fields: {
                interestRate,
                regZAPR,
                term,
                paymentAmount,
                finalPaymentDue,
                finalPaymentAmount,
            },
        });
    }, [
        interestRate,
        regZAPR,
        term,
        paymentAmount,
        finalPaymentDue,
        finalPaymentAmount,
    ]);

    const rollPaymentValues = {
        ...state,
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            aria-labelledby="roll-payment-dialog-title"
            aria-describedby="roll-payment-dialog-description"
        >
            {loading && (<div className={classes.loadingSpinner}><LoadingMask /></div>)}
            <DialogAppBar title="Roll Payment" onClose={toggleModal} iconSize="sm" titleVariant="h4" />
            <DialogContent className={classes.dialogContent} dividers>
                <Grid container className={classes.box}>
                    <Form.Group>
                        <Form.Label component="legend">Roll payment to:</Form.Label>
                        <Row noGutters className={classes.styleColLeft}>
                            <Col className={classes.alignText} xs={4}>
                                <Form.Check
                                    className={classes.radio}
                                    type="radio"
                                    name="rollPaymentTo"
                                    id="rollPaymentTo1"
                                    value={RollPaymentTo.PAYMENT}
                                    label="Payment:"
                                    checked={state.rollPaymentTo === RollPaymentTo.PAYMENT}
                                    onChange={() => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'rollPaymentTo', value: RollPaymentTo.PAYMENT })}
                                />
                            </Col>
                            <Col className={classes.alignText} xs={4}>
                                <InputNumber
                                    disabled={state.rollPaymentTo === RollPaymentTo.PAYMENT}
                                    value={state.paymentAmount}
                                    onChange={(value) => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'paymentAmount', value })}
                                    placeholder="0.00"
                                    thousandSeparator
                                    max={maximumPaymentOverride}
                                    showCurrency
                                />
                            </Col>
                        </Row>
                        <Row noGutters className={classes.styleColLeft}>
                            <Col className={classes.alignText} xs={4}>
                                <Form.Check
                                    className={classes.radio}
                                    type="radio"
                                    name="rollPaymentTo"
                                    id="rollPaymentTo2"
                                    value={RollPaymentTo.TERM}
                                    label="Term:"
                                    checked={state.rollPaymentTo === RollPaymentTo.TERM}
                                    onChange={() => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'rollPaymentTo', value: RollPaymentTo.TERM })}
                                />
                            </Col>
                            <Col className={classes.alignText} xs={4}>
                                <InputNumber
                                    disabled={state.rollPaymentTo === RollPaymentTo.TERM}
                                    value={state.term}
                                    onChange={(value) => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'term', value })}
                                    placeholder="0"
                                    decimalScale={0}
                                    thousandSeparator
                                />
                            </Col>
                        </Row>
                        <Row noGutters className={classes.styleColLeft}>
                            <Col className={classes.alignText} xs={4}>
                                <Form.Check
                                    className={classes.radio}
                                    type="radio"
                                    name="rollPaymentTo"
                                    id="rollPaymentTo3"
                                    value={RollPaymentTo.INTEREST}
                                    label="Rate:"
                                    checked={state.rollPaymentTo === RollPaymentTo.INTEREST}
                                    onChange={() => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'rollPaymentTo', value: RollPaymentTo.INTEREST })}
                                />
                            </Col>
                            <Col className={classes.alignText} xs={4}>
                                <InputNumber
                                    disabled={state.rollPaymentTo === RollPaymentTo.INTEREST}
                                    value={state.interestRate}
                                    onChange={(value) => dispatch({ type: ACTION_TYPE.SET_FIELD, field: 'interestRate', value })}
                                    placeholder="0.00"
                                    thousandSeparator
                                    decimalScale={4}
                                />
                            </Col>
                        </Row>
                    </Form.Group>
                </Grid>
                <Row noGutters className={classes.styleColLeft}>
                    <Col className={classes.alignLabel} xs={4}>
                        <Form.Label className={classes.labelStyle}>APR:</Form.Label>
                    </Col>
                    <Col className={classes.alignText} xs={4}>
                        <InputNumber
                            disabled
                            value={regZAPR}
                            placeholder="0.00"
                            thousandSeparator
                            decimalScale={4}
                        />
                    </Col>
                </Row>

                <Form.Row>
                    <Col>
                        <Form.Group>
                            <Form.Label className={classes.labelStyle}>First Payment Date</Form.Label>
                            <InputNumber
                                onChange={(value) => dispatch({ type: ACTION_TYPE.SET_FIRSTPAYMENT_DAYS, field: 'firstPaymentDueDays', value })}
                                value={state.firstPaymentDueDays}
                                placeholder="0"
                                decimalScale={0}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="full-width-date-picker">
                            <Form.Label className={clsx(classes.labelStyle, classes.hidden)}>First Payment Date:</Form.Label>
                            <DatePicker
                                onChange={(value) => dispatch({ type: ACTION_TYPE.SET_FIRSTPAYMENT_DATE, field: 'firstPaymentDue', value })}
                                minDate={soldDate}
                                popperContainer={CalendarContainer}
                                className="form-control"
                                placeholderText="mm/dd/yyyy"
                                selected={state.firstPaymentDue}
                            />
                        </Form.Group>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Col>
                        <Form.Group>
                            <Form.Label className={classes.labelStyle}>Final Payment Amount</Form.Label>
                            <InputNumber
                                disabled
                                showCurrency
                                fixedDecimalScale
                                value={finalPaymentAmount}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="full-width-date-picker">
                            <Form.Label className={classes.labelStyle}>Final Payment Date</Form.Label>
                            <DatePicker
                                disabled
                                className="form-control"
                                placeholderText="mm/dd/yyyy"
                                selected={finalPaymentDue}
                            />
                        </Form.Group>
                    </Col>
                </Form.Row>
            </DialogContent>
            <DialogActions
                titlePrimary="Apply"
                onClickPrimary={toggleModal}
                onClickSecondary={() => { resetDealInformation(); toggleModal(); }}
                disablePrimaryButton={
                    !(paymentAmount >= minimumPaymentOverride && paymentAmount <= maximumPaymentOverride)
                    || !rollPaymentApplyEnabled
                    || loading
                }
                primaryButtonTooltip={!rollPaymentApplyEnabled ? tooltip : null}
                useAdditionalButton
                titleAdditional="Calculate"
                classAdditionalButton={clsx(classes.button, classes.containedGreen, classes.calculateButton)}
                onClickAdditional={() => {
                    calculateDeal(rollPaymentValues);
                    dispatch({ type: ACTION_TYPE.RESET_CALCULATE });
                }}
                disableAdditionalButton={!state.enableCalculate}
            />
        </Dialog>
    );
};

RollPaymentDialog.propTypes = {
    toggleModal: PropTypes.func.isRequired,
    paymentAmount: PropTypes.number.isRequired,
    financeAmount: PropTypes.number.isRequired,
    finalPaymentAmount: PropTypes.number.isRequired,
    finalPaymentDue: PropTypes.object.isRequired,
    resetDealInformation: PropTypes.func.isRequired,
    minimumPaymentOverride: PropTypes.number.isRequired,
    term: PropTypes.number.isRequired,
    regZAPR: PropTypes.number.isRequired,
    soldDate: PropTypes.object.isRequired,
    interestRate: PropTypes.number.isRequired,
    firstPaymentDueDays: PropTypes.number.isRequired,
    firstPaymentDue: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    calculateDeal: PropTypes.func,
    rollPaymentApplyEnabled: PropTypes.bool,
    rollPaymentTo: PropTypes.string,
};

RollPaymentDialog.defaultProps = {
    loading: false,
    rollPaymentApplyEnabled: true,
    calculateDeal: () => null,
    rollPaymentTo: RollPaymentTo.PAYMENT,
};

export default RollPaymentDialog;
